import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../components/ui/PageHeader";
import StatCard from "../../components/ui/StatCard";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import { getDashboardRequest, DashboardEventStat, DashboardRecentOrder } from "../../services/api/dashboard.api";
import {getOrdersRequest} from "../../services/api/orders.api";
import {OrderItem} from "../../services/types/orders.types";

const formatCurrency = (value: string): string => {
    const numberValue = Number(value);

    if (Number.isNaN(numberValue)) {
        return value;
    }

    return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(numberValue);
};

const formatDateTime = (value: string): string => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};
const createInitialDashboardState = () => ({
    today: { orders_count: 0, orders_sum: "0" },
    week: { orders_count: 0, orders_sum: "0" },
    month: { orders_count: 0, orders_sum: "0" },
    year: { orders_count: 0, orders_sum: "0" },
    events: [] as DashboardEventStat[],
    recent_orders: [] as DashboardRecentOrder[],
});


const DashboardPage: React.FC = () => {
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);
    const [pageError, setPageError] = useState<string | null>(null);
    const [data, setData] = useState(createInitialDashboardState);
    const [items, setItems] = useState<OrderItem[]>([]);


    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            setPageError(null);

            try {
                const response = await getDashboardRequest();
                setData(response);
            } catch (error) {
                const message =
                    error instanceof Error ? error.message : "Не удалось загрузить дашборд";
                setPageError(message);
            } finally {
                setIsLoading(false);
            }
        };

        const loadOrders = async () => {
            setIsLoading(true);
            setPageError(null);
            try {
                const response = await getOrdersRequest(1, 5, {});
                setItems(response.items);
            } catch (error) {
                setPageError(error instanceof Error ? error.message : "Ошибка загрузки заказов");
            } finally {
                setIsLoading(false);
            }
        };

        void loadData();
        void loadOrders();
    }, []);



    const { today, week, month, year, events, recent_orders } = data;

    const nonEmptyEvents = useMemo(() => {
        return events.filter(
            (item) => item.total_tickets > 0 || item.sold_tickets > 0 || Number(item.sold_sum) > 0
        );
    }, [events]);

    return (
        <div className="page-content">
            <PageHeader
                title="Dashboard"
                description="Краткая сводка по продажам и мероприятиям, а также последние заказы."
            />

            <section className="stats-grid">
                <StatCard
                    label="Сегодня (сумма)"
                    value={formatCurrency(today.orders_sum)}
                    change={`${today.orders_count} заказ(ов)`}
                />
                <StatCard
                    label="Неделя (сумма)"
                    value={formatCurrency(week.orders_sum)}
                    change={`${week.orders_count} заказ(ов)`}
                />
                <StatCard
                    label="Месяц (сумма)"
                    value={formatCurrency(month.orders_sum)}
                    change={`${month.orders_count} заказ(ов)`}
                />
                <StatCard
                    label="Год (сумма)"
                    value={formatCurrency(year.orders_sum)}
                    change={`${year.orders_count} заказ(ов)`}
                />
            </section>

            {pageError ? (
                <div className="form-global-error">{pageError}</div>
            ) : null}

            {isLoading ? (
                <section className="dashboard-grid">
                    <Card>
                        <div className="table-skeleton">
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                        </div>
                    </Card>

                    <Card>
                        <div className="table-skeleton">
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                        </div>
                    </Card>
                </section>
            ) : (
                <section className="dashboard-grid">
                    <Card>
                        <div className="section-toolbar">
                            <div>
                                <h3 className="card-title">Мероприятия</h3>
                                <p className="card-text">
                                    Компактный список туров с общим количеством мест, проданными
                                    билетами и оборотом.
                                </p>
                            </div>
                        </div>

                        {nonEmptyEvents.length === 0 ? (
                            <div className="empty-block">
                                <h3 className="empty-block-title">Нет данных по мероприятиям</h3>
                                <p className="empty-block-text">
                                    Продажи ещё не начались или все туры пока без билетов.
                                </p>
                            </div>
                        ) : (
                            <div className="table-wrap dashboard-events-table-wrap">
                                <table className="data-table dashboard-events-table">
                                    <thead>
                                    <tr>
                                        <th>Мероприятие</th>
                                        <th>Места</th>
                                        <th>Продано</th>
                                        <th>Выручка</th>
                                        <th>Средний чек</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {nonEmptyEvents.map((event) => {
                                        const utilization =
                                            event.total_tickets > 0
                                                ? Math.round(
                                                    (event.sold_tickets / event.total_tickets) * 100
                                                )
                                                : 0;

                                        return (
                                            <tr key={event.tour_id}>
                                                <td>
                                                    <div className="table-primary-cell">
                                                        <span className="table-primary-text">
                                                            {event.tour_name}
                                                        </span>
                                                        <span className="table-secondary-text">
                                                            {utilization}% продано
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>{event.total_tickets}</td>
                                                <td>{event.sold_tickets}</td>
                                                <td>{formatCurrency(event.sold_sum)}</td>
                                                <td>
                                                    {Number(event.average_check) > 0
                                                        ? formatCurrency(event.average_check)
                                                        : "—"}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>

                    <Card>
                        <div className="section-toolbar">
                            <div>
                                <h3 className="card-title">Последние заказы</h3>
                                <p className="card-text">
                                    Пять последних заказов. Редактирование доступно на странице заказов.
                                </p>
                            </div>

                            <div className="section-toolbar-controls">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => navigate("/orders")}
                                >
                                    Все заказы
                                </Button>
                            </div>
                        </div>

                        {recent_orders.length === 0 ? (
                            <div className="empty-block">
                                <h3 className="empty-block-title">Заказов пока нет</h3>
                                <p className="empty-block-text">
                                    Как только появятся первые продажи, последние заказы появятся здесь.
                                </p>
                            </div>
                        ) : (
                            <div className="table-wrap dashboard-orders-table-wrap">
                                <table className="data-table dashboard-orders-table">
                                    <thead>
                                    <tr>
                                        <th>Номер</th>
                                        <th>Дата</th>
                                        <th>Событие</th>
                                        <th>Клиент</th>
                                        <th>Сумма</th>
                                        <th>Оплата</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {items.map((order) => (
                                        <tr
                                            key={order.id}
                                            onClick={() => {
                                                navigate(`/orders?orderId=${order.id}`);
                                            }}
                                        >
                                            <td>
                                                <div className="table-primary-cell">
                                                    <span className="table-primary-text">
                                                        {order.order_number}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>{formatDateTime(order.created_at)}</td>
                                            <td>
                                                {order.tours.map((tour) => (
                                                    <div>
                                                        <span className="table-primary-text">{tour.tour_name_ru}</span><br/><span className="table-secondary-text">Билетов {tour.items.length}</span>
                                                    </div>
                                                ))}
                                            </td>
                                            <td>
                                                <span className="table-secondary-text">
                                                    {order.customer_info?.first_name}{" "}
                                                    {order.customer_info?.last_name}
                                                </span>
                                                <div className="table-secondary-text">
                                                    {order.customer_info?.email}
                                                </div>
                                            </td>
                                            <td>{formatCurrency(order.total_price)}</td>
                                            <td>
                                                <span
                                                    className={`status-badge ${
                                                        order.is_paid
                                                            ? "status-badge-active"
                                                            : "status-badge-inactive"
                                                    }`}
                                                >
                                                    {order.is_paid ? "Оплачен" : "Не оплачен"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>
                </section>
            )}
        </div>
    );
};

export default DashboardPage;
